import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Home, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import company from '../config/company';

/**
 * Dedicated thank-you page reached after ANY successful lead submission.
 *
 * Purpose:
 *   - Single stable URL (/bedankt) so Google Ads can register a conversion
 *     when the user lands here, regardless of which form they filled in
 *     (property detail, homepage capture, exit-intent popup, contact page…).
 *   - Friendly, on-brand message reassuring the visitor that an advisor will
 *     reach out — turns a transactional moment into a relationship signal.
 *
 * SEO:
 *   noindex'd via the SEO component so it never shows up in organic results
 *   (Google Ads conversion pages should never compete in SERP).
 *
 * Tracking:
 *   Pushes a single `conversion` dataLayer event on mount. The Google Ads
 *   conversion tag in GTM can listen to this OR simply match the URL pattern
 *   /bedankt — both work; the dataLayer push gives you the cleaner GTM trigger.
 */
const ThankYou = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';

  useEffect(() => {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    window.dataLayer.push({
      event: 'conversion',
      conversion_type: 'lead_submitted',
      page_path: '/bedankt',
    });
    // Google Ads global tag — fires the conversion via gtag if it's loaded
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: 'lead_submitted' });
    }
  }, []);

  return (
    <div style={styles.page} data-testid="thankyou-page">
      <SEO
        titleKey="seo.contactTitle"
        descriptionKey="seo.contactDescription"
        noindex
      />
      <div style={styles.container}>
        <div style={styles.iconWrap}>
          <CheckCircle2 size={56} color="#22C55E" />
        </div>

        <h1 style={styles.title} data-testid="thankyou-title">
          {isNL ? 'Bedankt voor uw aanvraag!' : 'Thank you for your request!'}
        </h1>

        <p style={styles.lead}>
          {isNL
            ? 'Een van onze adviseurs neemt zo spoedig mogelijk persoonlijk contact met u op. Meestal binnen 24 uur, vaak veel sneller.'
            : 'One of our advisors will personally contact you as soon as possible. Usually within 24 hours, often much faster.'}
        </p>

        <div style={styles.checkList}>
          <Item
            text={isNL
              ? 'Wij bekijken uw wensen en stellen een persoonlijke selectie samen.'
              : 'We review your wishes and prepare a hand-picked selection.'}
          />
          <Item
            text={isNL
              ? 'U ontvangt antwoord via e-mail, telefoon of WhatsApp — zoals u verkiest.'
              : 'You will receive a response via email, phone or WhatsApp — whichever you prefer.'}
          />
          <Item
            text={isNL
              ? 'Indien gewenst plannen wij direct een bezichtiging in Spanje.'
              : 'If desired, we can immediately schedule a viewing in Spain.'}
          />
        </div>

        <div style={styles.cta}>
          <a
            href={company.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.btnWhatsapp}
            data-testid="thankyou-whatsapp"
          >
            <MessageCircle size={18} />
            {isNL ? 'Direct contact via WhatsApp' : 'Direct contact via WhatsApp'}
          </a>
          <a
            href={`mailto:${company.email}`}
            style={styles.btnEmail}
            data-testid="thankyou-email"
          >
            <Mail size={18} />
            {company.email}
          </a>
        </div>

        <div style={styles.divider} />

        <p style={styles.subnote}>
          {isNL
            ? 'In de tussentijd kunt u ons aanbod blijven verkennen:'
            : 'In the meantime, feel free to keep exploring our properties:'}
        </p>
        <div style={styles.links}>
          <Link to="/woningen" style={styles.linkBtn} data-testid="thankyou-back-woningen">
            <Home size={16} /> {isNL ? 'Bekijk al onze woningen' : 'Browse all properties'}
          </Link>
          <Link to="/" style={styles.linkMuted}>
            {isNL ? 'Terug naar de homepage' : 'Back to homepage'}
          </Link>
        </div>
      </div>
    </div>
  );
};

const Item = ({ text }) => (
  <div style={styles.checkItem}>
    <CheckCircle2 size={16} color="#C5A059" style={{ marginTop: '2px', flexShrink: 0 }} />
    <span>{text}</span>
  </div>
);

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#FAF9F6',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4rem 1.5rem',
  },
  container: {
    maxWidth: '620px',
    width: '100%',
    backgroundColor: 'white',
    padding: '3rem 2.5rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    borderTopWidth: '4px',
    borderTopStyle: 'solid',
    borderTopColor: '#C5A059',
  },
  iconWrap: { textAlign: 'center', marginBottom: '1.5rem' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
    color: '#1A1918',
    textAlign: 'center',
    margin: '0 0 1rem',
    lineHeight: 1.2,
    fontWeight: 500,
  },
  lead: {
    color: '#585755',
    fontSize: '1.05rem',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: '0 0 2rem',
  },
  checkList: {
    display: 'flex', flexDirection: 'column', gap: '0.65rem',
    backgroundColor: '#FAF9F6',
    padding: '1.25rem 1.5rem',
    marginBottom: '2rem',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#C5A059',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.6rem',
    color: '#585755',
    fontSize: '0.95rem',
    lineHeight: 1.55,
  },
  cta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    marginBottom: '2rem',
  },
  btnWhatsapp: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: '#25D366',
    color: 'white',
    textDecoration: 'none',
    padding: '1rem 1.5rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontFamily: "'Manrope', sans-serif",
  },
  btnEmail: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: '#1A1918',
    color: 'white',
    textDecoration: 'none',
    padding: '1rem 1.5rem',
    fontSize: '0.88rem',
    fontWeight: 500,
    fontFamily: "'Manrope', sans-serif",
  },
  divider: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#EAE5DD',
    margin: '0 -2.5rem 2rem',
  },
  subnote: {
    color: '#8C8B88',
    fontSize: '0.88rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  links: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    alignItems: 'center',
  },
  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#C5A059',
    textDecoration: 'none',
    fontSize: '0.92rem',
    fontWeight: 600,
    padding: '0.5rem 1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#C5A059',
  },
  linkMuted: {
    color: '#8C8B88',
    textDecoration: 'underline',
    fontSize: '0.85rem',
  },
};

export default ThankYou;
